import { z } from '../zod';
import {
  PricingTypeEnumValidationSchema,
  ProductRoleEnumValidationSchema,
  ProductTypeEnumValidationSchema,
  TaxBehaviourEnumValidationSchema,
  VariantSelectionTypeEnumValidationSchema,
  VariantStatusEnumValidationSchema,
  BadgeIconEnumValidationSchema,
  FeatureIconEnumValidationSchema,
} from './enum';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';

const MediaResponseSchema = z.object({
  fileId: z.string().nullish().meta({ description: 'File ID of the media asset.' }),
  url: z.string().nullish().meta({ description: 'CDN URL of the media asset.' }),
  name: z.string().nullish(),
  width: z.number().nullish(),
  height: z.number().nullish(),
  size: z.number().nullish(),
});

const BillingCycleAnchorConfigResponseSchema = z
  .object({
    enabled: z.boolean().nullish(),
    dayOfMonth: z.number().min(1).max(31).nullish().meta({
      description: 'Day of month the billing cycle is anchored to.',
    }),
    month: z.number().min(1).max(12).nullish(),
  })
  .nullish();

export const RecurringResponseSchema = z.object({
  interval: z.enum(['day', 'week', 'month', 'year']).nullish().meta({
    description: 'Billing interval (day, week, month, year).',
    example: 'month',
  }),
  intervalCount: z.number().nullish().meta({
    description: 'Number of intervals between billings.',
    example: 1,
  }),
  trialPeriodDays: z.number().nullish().meta({
    description: 'Trial period in days.',
  }),
  billingCycleAnchorConfig: BillingCycleAnchorConfigResponseSchema,
  startDate: z.iso.datetime().nullish().meta({
    description: 'The date on which the subscription begins.',
  }),
});

export const PaymentPlanResponseSchema = RecurringResponseSchema.extend({
  planIterations: z.number().nullish().meta({
    description: 'Number of billing cycles after which the plan completes.',
  }),
});

export const BasePriceResponseSchema = z.object({
  amount: z.number().meta({
    description: 'Price in smallest currency unit (cents).',
    example: 4900,
  }),
  currency: z.string().meta({
    description: 'Lowercase ISO 4217 currency code.',
    example: 'usd',
  }),
  payWhatYouWant: z.boolean().nullish().meta({
    description: 'Whether pay-what-you-want pricing is enabled.',
  }),
  discountedFromPrice: z.number().nullish().meta({
    description: 'Original price before discount, in cents.',
  }),
  setupFee: z.number().nullish().meta({
    description: 'One-time setup fee in cents.',
  }),
  setupFeeMultipliesWithQuantity: z.boolean().nullish(),
  recurring: RecurringResponseSchema.nullish().meta({
    description: 'Recurring billing configuration. Never present together with paymentPlan.',
  }),
  paymentPlan: PaymentPlanResponseSchema.nullish().meta({
    description:
      'Payment plan configuration — recurring billing that completes after `planIterations` cycles. Never present together with recurring.',
  }),
});

export const ProductPriceResponseSchema = BasePriceResponseSchema.extend({
  pricingType: PricingTypeEnumValidationSchema.nullish().meta({
    description: 'Deprecated — superseded by `prices[]`. Retained for backward compatibility.',
    example: 'single',
    deprecated: true,
  }),
});

const VariantOptionResponseSchema = z.object({
  id: z.string().nullish(),
  name: z.string(),
  status: VariantStatusEnumValidationSchema.nullish(),
  description: z.string().nullish(),
  image: z
    .object({
      fileId: z.string().nullish(),
      url: z.string().nullish(),
      name: z.string().nullish(),
      width: z.number().nullish(),
      height: z.number().nullish(),
      size: z.number().nullish(),
    })
    .nullish(),
  additionalChargeAmount: z.number().nullish(),
  sku: z.string().nullish(),
  stock: z.number().nullish(),
  type: z.enum(['one_time', 'recurring', 'pay_what_you_want']).nullish(),
  payWhatYouWant: z.boolean().nullish(),
  pwywSuggestedPrice: z.number().nullish(),
  fileIds: z.array(ObjectIdStringValidationSchema).nullish(),
});

const VariantResponseSchema = z.object({
  id: z.string().nullish(),
  name: z.string(),
  required: z.boolean().nullish(),
  status: VariantStatusEnumValidationSchema.nullish(),
  options: z.array(VariantOptionResponseSchema).nullish(),
  order: z.number().nullish(),
  reference: z.string().nullish(),
  selectionType: VariantSelectionTypeEnumValidationSchema.nullish(),
  increasesWithQuantity: z.boolean().nullish(),
  useVariantOptionSkus: z.boolean().nullish(),
  manageVariantOptionStock: z.boolean().nullish(),
  priceIds: z.array(ObjectIdStringValidationSchema).nullish().meta({
    description:
      'Price IDs this variant group is scoped to. Present only when the product has multiple prices. Customers see this variant group only when the corresponding price is selected.',
  }),
  hidden: z.boolean().nullish(),
  preselect: z
    .object({
      enabled: z.boolean().nullish(),
      optionId: z.string().nullish(),
      quantity: z.number().nullish(),
    })
    .nullish(),
  showHideLogic: z
    .object({
      enabled: z.boolean().nullish(),
      comparison: z.enum(['is', 'is_not', 'is_empty', 'is_not_empty', 'contains']).nullish(),
      value: z.string().nullish(),
      element: z.object({ elementId: z.string().nullish() }).nullish(),
    })
    .nullish(),
  layout: z
    .object({
      variantOptionLayout: z.enum(['list', 'grid']).nullish(),
      collapseVariantOptions: z.boolean().nullish(),
      variantOptionColumns: z.number().nullish(),
      showVariantName: z.boolean().nullish(),
      showVariantOptionNames: z.boolean().nullish(),
      showVariantOptionPrices: z.boolean().nullish(),
      showVariantOptionPriceSign: z.boolean().nullish(),
      showPwywPlaceholder: z.boolean().nullish(),
      textAlign: z.enum(['left', 'center', 'stack', '']).nullish(),
      spacing: z.enum(['comfortable', 'compact', '']).nullish(),
      imageSize: z.enum(['small', 'medium', 'large', '']).nullish(),
    })
    .nullish(),
});

export const PriceResponseValidationSchema = BasePriceResponseSchema.extend({
  id: z.string().meta({
    description: 'Unique price identifier.',
    example: '507f1f77bcf86cd799439011',
  }),
  label: z.string().nullish().meta({
    description: 'Optional seller-facing label (e.g. "Monthly", "Annual").',
  }),
  badge: z.string().nullish().meta({
    description: 'Optional short label shown over the price card.',
  }),
  badgeIcon: BadgeIconEnumValidationSchema.nullish().meta({
    description: 'Optional icon shown before the badge text.',
  }),
  order: z.number().nullish().meta({
    description: 'Dashboard display order.',
  }),
  reference: z.string().nullish().meta({
    description:
      'Stable label for this price, sent to exports, Zapier, Google Sheets and webhooks as `priceReference`. A generated reference can contain the amount the price was created with, such as `price_one_time_fixed_5000`. It is not updated when the amount changes, and does not need to be.',
  }),
  enabled: z.boolean().meta({
    description: 'Whether this price is selectable by customers.',
  }),
  hidden: z.boolean().nullish().meta({
    description: 'Hide this price. Hidden prices can still be shown using query parameters.',
  }),
  isDefault: z.boolean().meta({
    description:
      'Whether checkout pre-selects this price: the entry `defaultPriceId` names. False on every price when the product has several prices and no default.',
  }),
  billingType: z.enum(['one_time', 'recurring']).meta({
    description:
      'Billing type. Immutable once the price exists (see the pricing-type identity lock).',
    example: 'one_time',
  }),
  pwywSuggestedPrice: z.number().nullish().meta({
    description: 'Suggested price in cents for PWYW prices.',
  }),
}).meta({ description: 'A single price entry on a multi-price product.' });

const ProductDiscountResponseSchema = z.object({
  quantityCondition: z.enum(['checkout_quantity', 'variant_option_quantity']),
  variantOptionIds: z.array(ObjectIdStringValidationSchema).nullish(),
  minQuantity: z.number().nullish(),
  maxQuantity: z.number().nullish(),
  percentOff: z.number().nullish(),
  amountOff: z.number().nullish(),
});

export const ProductResponseValidationSchema = z.object({
  id: z.string().meta({ description: 'Unique identifier for the product.' }),
  type: ProductTypeEnumValidationSchema.meta({
    description: 'Product type.',
  }),
  title: z.string().meta({ description: 'Product title.', example: 'Premium Course' }),
  description: z.string().nullish().meta({ description: 'Product description as HTML.' }),
  shortDescription: z.string().nullish().meta({
    description: 'Plain-text one-liner, shown where the full description is too long.',
  }),
  callToActionText: z.string().nullish().meta({
    description: 'Accept copy shown on an order-bump card, e.g. "Yes! Add gift wrapping".',
  }),
  headingText: z.string().nullish().meta({
    description: 'Heading shown above an order-bump card, e.g. "Special one time offer".',
  }),
  features: z
    .array(
      z.object({
        text: z.string().meta({ description: 'One line of the list.' }),
        icon: FeatureIconEnumValidationSchema.nullish().meta({
          description:
            'Icon leading the line. Unset, the line leads with a check; `none` draws no icon.',
        }),
      }),
    )
    .nullish()
    .meta({
      description:
        'Short list of what the customer gets, shown on an order-bump card above the description.',
    }),
  calloutText: z.string().nullish().meta({
    description:
      'Call out box shown on an order-bump card under the description. Sanitised HTML: links and inline emphasis only.',
  }),
  calloutIcon: BadgeIconEnumValidationSchema.nullish().meta({
    description: 'Icon leading the call out box.',
  }),
  // Nullish, unlike the API's own OpenAPI schema: this validates responses from
  // a live upstream that may not have deployed the field yet.
  role: ProductRoleEnumValidationSchema.nullish(),
  sku: z.string().nullish(),
  price: ProductPriceResponseSchema,
  prices: z
    .array(PriceResponseValidationSchema)
    .nullish()
    .meta({
      description:
        'Explicit price list for this product. Populated when the product was created with `prices[]`. Each entry includes an `isDefault` flag. ' +
        'Use `defaultPriceId` to find the pre-selected price. Disabled prices are filtered from customer-facing reads but appear in seller API responses.',
    }),
  defaultPriceId: z.string().nullish().meta({
    description:
      'The price pre-selected at checkout: the saved default while it is enabled, or the only enabled price. Null when the product has several prices and no default, so the buyer chooses one.',
  }),
  pricePicker: z
    .object({
      title: z.string().nullish().meta({ description: 'Heading shown above the price options.' }),
      description: z
        .string()
        .nullish()
        .meta({ description: 'Text shown above the price options.' }),
      shortenDescription: z.boolean().nullish().meta({
        description: 'Whether long price descriptions are cut behind a Show more link.',
      }),
      priceDisplayLabels: z.record(z.string(), z.record(z.string(), z.string())).nullish(),
      priceDisplayDefaults: z.record(z.string(), z.string()).nullish(),
      priceDisplayTabOrder: z.record(z.string(), z.array(z.string())).nullish(),
    })
    .nullish()
    .meta({
      description:
        'Price picker settings: the heading and description above the options, Show more / less, and tab labels, defaults and order.',
    }),
  taxBehavior: TaxBehaviourEnumValidationSchema.nullish(),
  taxCode: z.string().nullish(),
  fixedTaxRateIds: z.array(z.string()).max(1).nullish().meta({
    description: 'Applied Tax Rate IDs for this product.',
  }),
  media: z.array(MediaResponseSchema).nullish().meta({ description: 'Product images.' }),
  fileIds: z.array(ObjectIdStringValidationSchema).nullish(),
  variants: z.array(VariantResponseSchema).nullish().meta({ description: 'Product variants.' }),
  discounts: z
    .array(ProductDiscountResponseSchema)
    .nullish()
    .meta({ description: 'Bulk discount tiers.' }),
  generateLicenseKeys: z.boolean().nullish(),
  limitSubscriptions: z
    .object({
      enabled: z.boolean().nullish(),
      limitSubscriptionsStripe: z.object({ enabled: z.boolean().nullish() }).nullish(),
    })
    .nullish()
    .meta({ description: 'Duplicate-subscription prevention settings for this product.' }),
  limitPayments: z
    .object({
      enabled: z.boolean().nullish(),
      enabledAt: z.string().nullish(),
    })
    .nullish()
    .meta({
      description:
        'Duplicate-payment prevention settings for this product. Applies to one-time payments only.',
    }),
  enableFileAccessForInactiveSubscriptions: z.boolean().nullish().meta({
    description: 'Whether customers with inactive subscriptions can still access file downloads.',
  }),
  variantsRequired: z.boolean().nullish(),
  stock: z.number().nullish(),
  hasUnlimitedStock: z.boolean().nullish(),
  createdAt: z.string().meta({ description: 'ISO 8601 timestamp when the product was created.' }),
  updatedAt: z
    .string()
    .meta({ description: 'ISO 8601 timestamp when the product was last updated.' }),
});

export const ProductDetailResponseValidationSchema = z
  .object({
    data: ProductResponseValidationSchema,
  })
  .meta({ description: 'A single product record.' });

export type ProductResponse = z.infer<typeof ProductResponseValidationSchema>;
export type ProductDetailResponse = z.infer<typeof ProductDetailResponseValidationSchema>;
